import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ChartKind } from '../../utils/api/coatype';

// ── The badge a client account's type wears (batch 5 round 4) ───────────────────────────────────
// ⛔ NO RULE OF ITS OWN. `kind` is the SERVER's answer for the stored type word (server/src/services/
// coaRules.ts, sent on every row of GET /accounting/chart); this only picks a look for it. The type
// word itself is the customer's and is shown verbatim (lang="en") in every language. The same five
// looks as Admin → Chart template (src/components/Admin/CoaMaster.tsx), so a Header reads the same on
// the master template and on a client's chart.

type Badged = 'header' | 'control' | 'contra' | 'system' | 'reference';
const STYLE: Record<Badged, { icon: string; bg: string; fg: string; border: string }> = {
  header: { icon: 'ti-folder', bg: 'var(--c-gray-100)', fg: 'var(--text-secondary)', border: '1px dashed var(--border-strong)' },
  control: { icon: 'ti-lock', bg: '#EFF6FF', fg: '#1D4ED8', border: '1px solid #BFDBFE' },
  contra: { icon: 'ti-arrows-exchange', bg: '#FFF7ED', fg: '#9A3412', border: '1px solid #FED7AA' },
  system: { icon: 'ti-cpu', bg: '#F5F3FF', fg: '#6D28D9', border: '1px solid #DDD6FE' },
  reference: { icon: 'ti-bookmark', bg: '#ECFDF5', fg: '#047857', border: '1px solid #A7F3D0' },
};
const isBadged = (k: string): k is Badged => Object.prototype.hasOwnProperty.call(STYLE, k);
const pill = (s: { bg: string; fg: string; border: string }): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0 7px', borderRadius: 10, fontSize: 11, fontWeight: 600,
  whiteSpace: 'nowrap', background: s.bg, color: s.fg, border: s.border,
});

/** The type word with its kind's look; a posting type is the plain word; '' (not classified) shows nothing. */
export function TypeBadge({ type, kind }: { type: string; kind: ChartKind }) {
  const { t } = useTranslation();
  if (!type) return null;
  if (!isBadged(kind)) {
    return <span data-type-badge={kind || 'unknown'} lang="en" title={kind === 'posting' ? t('coat_kind_posting') : undefined}
      style={{ fontSize: 11.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{type}</span>;
  }
  const s = STYLE[kind];
  return (
    <span data-type-badge={kind} data-kind={kind} lang="en" title={t(`coat_kind_${kind}`)} style={pill(s)}>
      <i className={`ti ${s.icon}`} style={{ fontSize: 12 }} aria-hidden />{type}
    </span>
  );
}

/** The account controls a subsidiary ledger (its `control` is ar / ap) — shown whatever its type says,
 *  because the control is a stored designation of its own, not something read off the type word. */
export function ControlBadge({ role }: { role: string }) {
  const { t } = useTranslation();
  if (!role) return null;
  return (
    <span data-type-badge="control-role" data-kind="control" title={t('coat_kind_control')} style={pill(STYLE.control)}>
      <i className={`ti ${STYLE.control.icon}`} style={{ fontSize: 12 }} aria-hidden />{t('coat_control_badge', { role: role.toUpperCase() })}
    </span>
  );
}
